import "@/App.css";
import { BrowserRouter, Navigate, Routes, Route, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AdminAuthProvider } from "@/context/AdminAuth";
import { StudyAuthProvider, useStudyAuth } from "@/context/StudyAuth";
import Layout from "@/components/layout/Layout";
import Home from "@/pages/Home";
import Product from "@/pages/Product";
import Preview from "@/pages/Preview";
import FAQ from "@/pages/FAQ";
import Refund from "@/pages/Refund";
import Disclaimer from "@/pages/Disclaimer";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Coverage from "@/pages/Coverage";
import LiveReview from "@/pages/LiveReview";
import Diagnostic from "@/pages/Diagnostic";
import DiagnosticResults from "@/pages/DiagnosticResults";
import Settings from "@/pages/Settings";
import AdminInbox from "@/pages/AdminInbox";
import AdminQuestions from "@/pages/AdminQuestions";
import AdminAccessCodes from "@/pages/AdminAccessCodes";
import AdminWebhookDeliveries from "@/pages/AdminWebhookDeliveries";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminTutorUsage from "@/pages/AdminTutorUsage";
import AdminFlashcards from "@/pages/AdminFlashcards";
import AdminQuality from "@/pages/AdminQuality";
import AdminStudents from "@/pages/AdminStudents";
import AdminLiveReview from "@/pages/AdminLiveReview";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import StudyLogin from "@/pages/study/StudyLogin";
import StudyDashboard from "@/pages/study/StudyDashboard";
import StudyLearn from "@/pages/study/StudyLearn";
import StudyLesson from "@/pages/study/StudyLesson";
import StudyPlan from "@/pages/study/StudyPlan";
import StudyQuestionBank from "@/pages/study/StudyQuestionBank";
import StudyPractice from "@/pages/study/StudyPractice";
import StudyMissed from "@/pages/study/StudyMissed";
import StudyBookmarks from "@/pages/study/StudyBookmarks";
import StudyMiniExams from "@/pages/study/StudyMiniExams";
import StudyFlashcards from "@/pages/study/StudyFlashcards";
import StudySprintRunner from "@/pages/study/StudySprintRunner";
import StudySprintResults from "@/pages/study/StudySprintResults";
import StudyExamWeek from "@/pages/study/StudyExamWeek";
import StudyFeedback from "@/pages/study/StudyFeedback";
import NotFound from "@/pages/NotFound";
import StudyShell from "@/components/study/StudyShell";
import UpgradeGate from "@/components/study/UpgradeGate";

function PremiumRoute({ children, title, description }) {
  const { isAuthed, hasFullAccess } = useStudyAuth();
  if (!isAuthed) return <Navigate to="/study/login" replace />;
  if (hasFullAccess) return children;
  return (
    <StudyShell>
      <UpgradeGate title={title} description={description} />
    </StudyShell>
  );
}

function StudySprintRoute() {
  const { sprintType } = useParams();
  return <StudySprintRunner sprintType={sprintType} />;
}

function App() {
  return (
    <div className="App">
      <AdminAuthProvider>
        <StudyAuthProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/product" element={<Product />} />
                <Route path="/preview" element={<Preview />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/refund" element={<Refund />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/coverage" element={<Coverage />} />
                <Route path="/live-review" element={<LiveReview />} />
                <Route path="/diagnostic" element={<Diagnostic />} />
                <Route path="/diagnostic/results" element={<DiagnosticResults />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/admin/inbox" element={<AdminInbox />} />
                <Route path="/admin/questions" element={<AdminQuestions />} />
                <Route path="/admin/access-codes" element={<AdminAccessCodes />} />
                <Route path="/admin/webhook-deliveries" element={<AdminWebhookDeliveries />} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/admin/tutor-usage" element={<AdminTutorUsage />} />
                <Route path="/admin/flashcards" element={<AdminFlashcards />} />
                <Route path="/admin/quality" element={<AdminQuality />} />
                <Route path="/admin/students" element={<AdminStudents />} />
                <Route path="/admin/live-review" element={<AdminLiveReview />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              {/* Study routes — own shell (no public nav/footer) */}
              <Route path="/login" element={<Navigate to="/study/login" replace />} />
              <Route path="/study/login" element={<StudyLogin />} />
              <Route path="/study/dashboard" element={<StudyDashboard />} />
              <Route path="/study/learn" element={<StudyLearn />} />
              <Route path="/study/learn/:lessonId" element={<StudyLesson />} />
              <Route
                path="/study/plan"
                element={(
                  <PremiumRoute
                    title="Build a complete plan to exam day."
                    description="Upgrade to turn your weak areas, exam date, and missed questions into a daily study plan."
                  >
                    <StudyPlan />
                  </PremiumRoute>
                )}
              />
              <Route path="/study/question-bank" element={<StudyQuestionBank />} />
              <Route path="/study/practice" element={<StudyPractice />} />
              <Route path="/study/missed" element={<StudyMissed />} />
              <Route path="/study/bookmarks" element={<StudyBookmarks />} />
              <Route
                path="/study/mini-exams"
                element={(
                  <PremiumRoute
                    title="Practice under real exam pressure."
                    description="Full access unlocks separate Illinois and National simulations, timed sprints, saved results, and review."
                  >
                    <StudyMiniExams />
                  </PremiumRoute>
                )}
              />
              <Route path="/study/flashcards" element={<StudyFlashcards />} />
              <Route
                path="/study/exam-week"
                element={(
                  <PremiumRoute title="Unlock your final exam-week plan.">
                    <StudyExamWeek />
                  </PremiumRoute>
                )}
              />
              <Route path="/study/feedback" element={<StudyFeedback />} />
              <Route
                path="/study/mini-exams/:sprintType"
                element={(
                  <PremiumRoute title="Unlock timed exam practice.">
                    <StudySprintRoute />
                  </PremiumRoute>
                )}
              />
              <Route
                path="/study/mini-exams/results/:attemptId"
                element={(
                  <PremiumRoute title="Unlock saved exam results and review.">
                    <StudySprintResults />
                  </PremiumRoute>
                )}
              />
            </Routes>
          </BrowserRouter>
        </StudyAuthProvider>
      </AdminAuthProvider>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export default App;
